import ImgLoading from "@/directives/img-loading";
import Ability from "@/plugins/ability";
import Analytics from "@/plugins/analytics";
import Loading from "@/plugins/loading";
import Map from "@/plugins/map";
import Sound from "@/plugins/sound";
import Toast from "@/plugins/toast";
import {
    cognitoCredentialsProvider,
    cognitoUserPoolsTokenProvider
} from "@aws-amplify/auth/cognito";
import "@aws-amplify/auth/enable-oauth-listener";
import { Amplify, defaultStorage } from "@aws-amplify/core";
import "leaflet/dist/leaflet.css";
import "sweetalert2/dist/sweetalert2.min.css";
import VueImg from "v-img";
import Vue from "vue";
import { LMap } from "vue2-leaflet";
import { Plugin } from "vue-fragment";
import VueMeta from "vue-meta";
import VueSweetalert2 from "vue-sweetalert2";

import {
    AUTH_REDIRECT_SIGN_IN,
    AUTH_REDIRECT_SIGN_OUT,
    BUSINESS_NAME,
    COGNITO_URL,
    COGNITO_USERS_POOLS_ID,
    COGNITO_USERS_POOLS_WEB_CLIENT_ID,
    FRANCHISE_DOMAIN,
    FRANCHISE_PORTAL_TITLE,
    FRANCHISE_URL,
    HAS_CUSTOM_SUBDOMAIN,
    HAS_INTERNAL_COMMISSIONS,
    IS_ENABLED_ECOMMERCE_OPTIONS,
    IS_ENABLED_GOOGLE_AUTH,
    IS_MULTI_TENANT,
    OPERATION_MODE,
    SITE_TYPE,
    TIMEZONE,
    VERSION
} from "@/common/constants";
import {
    addressFilter,
    cityFilter,
    currencyFilter,
    cutStringFilter,
    dateFilter,
    dateSimpleFilter,
    imagesLengthFilter,
    merchantInfoFilter,
    moneyFilter,
    percentFilter,
    transformStringFilter,
    trimFilter
} from "@/common/lib/filters";
import { isSessionExpiredRedirecting } from "@/common/lib/formatter";

import App from "./App.vue";
import "./assets/css/index.css";
import "./assets/css/tailwind.css";
import cashSound from "./assets/sounds/cash.wav";
import errorSound from "./assets/sounds/error.mp3";
import successSound from "./assets/sounds/success.mp3";
import wb from "./register-service-worker";
import router from "./router";
import store from "./store";

Vue.config.productionTip = true;

Vue.use(ImgLoading);
Vue.use(VueMeta);
Vue.use(VueSweetalert2);
const swalFire = Vue.prototype.$swal.fire.bind(Vue.prototype.$swal);
Vue.prototype.$swal.fire = (...args) =>
    isSessionExpiredRedirecting()
        ? Promise.resolve({ isDismissed: true })
        : swalFire(...args);
Vue.use(Plugin);
Vue.use(VueImg);
Vue.use(Ability);
Vue.use(Analytics);
Vue.use(Loading);
Vue.use(Toast);
Vue.use(Map);
Vue.use(Sound, store, {
    sounds: [
        {
            name: "cash",
            url: cashSound
        },
        {
            name: "error",
            url: errorSound
        },
        {
            name: "success",
            url: successSound
        }
    ]
});
Vue.filter("money", moneyFilter);
Vue.filter("city", cityFilter);
Vue.filter("trim", trimFilter);
Vue.filter("merchantInfo", merchantInfoFilter);
Vue.filter("transformString", transformStringFilter);
Vue.filter("currency", currencyFilter);
Vue.filter("percent", percentFilter);
Vue.filter("date", dateFilter);
Vue.filter("dateSimple", dateSimpleFilter);
Vue.filter("image", imagesLengthFilter);
Vue.filter("address", addressFilter);
Vue.filter("cut", cutStringFilter);
Vue.component("LMap", LMap);
Vue.component("AppButton", () => import("@/components/utils/AppButton.vue"));
Vue.component("ListSettingsButton", () =>
    import("@/components/utils/ListSettingsButton.vue")
);
Vue.component("CustomNavbar", () =>
    import("@/components/utils/CustomNavbar.vue")
);
Vue.component("CustomNavbarItem", () =>
    import("@/components/utils/CustomNavbarItem.vue")
);
Vue.component("CustomCheckbox", () =>
    import("@/components/utils/CustomCheckbox.vue")
);
Vue.component("CustomRadio", () =>
    import("@/components/utils/CustomRadio.vue")
);
Vue.component("CustomAvatar", () =>
    import("@/components/utils/CustomAvatar.vue")
);
Vue.component("CustomTooltip", () =>
    import("@/components/utils/CustomTooltip.vue")
);
Vue.component("DropdownPopover", () =>
    import("@/components/utils/DropdownPopover.vue")
);
Vue.component("CustomPagination", () =>
    import("@/components/utils/CustomPagination.vue")
);
Vue.component("CustomSwitch", () =>
    import("@/components/utils/CustomSwitch.vue")
);
Vue.prototype.$workbox = wb;
Vue.prototype.$enabledEcommerceOptions = IS_ENABLED_ECOMMERCE_OPTIONS;
Vue.prototype.$hasInternalCommissions = HAS_INTERNAL_COMMISSIONS;
Vue.prototype.$hasCustomSubdomain = HAS_CUSTOM_SUBDOMAIN;
Vue.prototype.$businessName = BUSINESS_NAME;
Vue.prototype.$siteVersion = VERSION;
Vue.prototype.$siteTitle = FRANCHISE_PORTAL_TITLE;
Vue.prototype.$siteType = SITE_TYPE;
Vue.prototype.$timezone = TIMEZONE;
Vue.prototype.$franchiseDomain = FRANCHISE_DOMAIN;
Object.defineProperty(Vue.prototype, "$publicUrl", {
    get() {
        const { tenantSlug } = store.state.auth;
        if (tenantSlug && FRANCHISE_DOMAIN && HAS_CUSTOM_SUBDOMAIN) {
            return `https://${tenantSlug}.${FRANCHISE_DOMAIN}`;
        }
        return FRANCHISE_URL || "";
    }
});
Vue.prototype.$operationMode = OPERATION_MODE;
Vue.prototype.$isMultitenant = IS_MULTI_TENANT;
Vue.prototype.$isPhone =
    navigator.userAgent.match(/Android/i) ||
    navigator.userAgent.match(/webOS/i) ||
    navigator.userAgent.match(/iPhone/i) ||
    navigator.userAgent.match(/iPad/i) ||
    navigator.userAgent.match(/iPod/i) ||
    navigator.userAgent.match(/BlackBerry/i) ||
    navigator.userAgent.match(/Windows Phone/i) ||
    false;

if (IS_ENABLED_GOOGLE_AUTH) {
    const authConfig = {
        Cognito: {
            userPoolId: COGNITO_USERS_POOLS_ID,
            userPoolClientId: COGNITO_USERS_POOLS_WEB_CLIENT_ID,
            loginWith: {
                oauth: {
                    domain: COGNITO_URL,
                    scopes: ["email", "openid", "profile"],
                    redirectSignIn: [AUTH_REDIRECT_SIGN_IN],
                    redirectSignOut: [AUTH_REDIRECT_SIGN_OUT],
                    responseType: "code"
                }
            }
        }
    };

    // Amplify se importa siempre de "@aws-amplify/*", nunca del wrapper
    // "aws-amplify": sus reexports .mjs hacen que webpack 4 resuelva por
    // "main" (dist/cjs) y el bundle termina con dos copias de cada singleton
    // (ver los alias en vue.config.js).
    //
    // Registrar los providers a mano es lo que hace por dentro el configure
    // del wrapper; sin ellos fetchAuthSession() devuelve tokens vacios. El
    // import de enable-oauth-listener es lo que cambia el `code` de la URL por
    // tokens al volver de Cognito: ningun otro entrypoint de auth lo trae.
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
    cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);

    Amplify.configure(
        { Auth: authConfig },
        {
            Auth: {
                tokenProvider: cognitoUserPoolsTokenProvider,
                credentialsProvider: cognitoCredentialsProvider
            }
        }
    );
}

new Vue({
    router,
    store,
    render: (h) => h(App)
}).$mount("#app");
